import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/layout/AdminLayout';
import { View, Text, ScrollView, RefreshControl, Platform, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import MetricCard from '../components/dashboard/MetricCard';
import QuickActions from '../components/dashboard/QuickActions';
import ActivityTimeline from '../components/dashboard/ActivityTimeline';
import { getDashboardAnalytics, getRecentActivities } from '../api/analytics';
import { getColor, getSpacing, getBorderRadius, getShadow } from '../theme/adminTheme';

const AdminPanelScreen = ({ navigation }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [analytics, setAnalytics] = useState({
    totalUsers: 0,
    totalParticipants: 0,
    totalEvents: 0,
    totalRevenue: 0,
    userGrowth: { change: 0 },
    participantGrowth: { change: 0 },
    eventGrowth: { change: 0 },
    revenueGrowth: { change: 0 },
  });
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch analytics data
      const analyticsResponse = await getDashboardAnalytics();
      if (analyticsResponse?.success && analyticsResponse?.data) {
        setAnalytics(analyticsResponse.data);
      }

      // Fetch recent activities
      const activitiesResponse = await getRecentActivities(10);
      if (activitiesResponse?.success && activitiesResponse?.data) {
        setActivities(activitiesResponse.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Use mock data if API fails
      setAnalytics({
        totalUsers: 1247,
        totalParticipants: 856,
        totalEvents: 24,
        totalRevenue: 125000,
        userGrowth: { change: 12.5 },
        participantGrowth: { change: 8.3 },
        eventGrowth: { change: 15.7 },
        revenueGrowth: { change: -3.2 },
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  return (
    <AdminLayout>
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.headerTitle}>Dashboard Overview</Text>
            <Text style={styles.headerSubtitle}>
              Welcome back! Here's what's happening today.
            </Text>
          </View>
          <View style={styles.headerActions}>
            <View style={styles.lastUpdated}>
              <Ionicons name="time-outline" size={16} color={getColor('gray.500')} />
              <Text style={styles.lastUpdatedText}>
                Updated {new Date().toLocaleTimeString()}
              </Text>
            </View>
          </View>
        </View>

        {/* Metrics Grid */}
        <View style={styles.metricsGrid}>
          <MetricCard
            title="Total Users"
            value={(analytics.totalUsers || 0).toLocaleString()}
            iconName="people"
            change={analytics.userGrowth?.change || 0}
            trend={(analytics.userGrowth?.change || 0) >= 0 ? 'up' : 'down'}
            color="primary"
          />
          <MetricCard
            title="Active Participants"
            value={(analytics.totalParticipants || 0).toLocaleString()}
            iconName="person"
            change={analytics.participantGrowth?.change || 0}
            trend={(analytics.participantGrowth?.change || 0) >= 0 ? 'up' : 'down'}
            color="success"
          />
          <MetricCard
            title="Total Events"
            value={(analytics.totalEvents || 0).toString()}
            iconName="calendar"
            change={analytics.eventGrowth?.change || 0}
            trend={(analytics.eventGrowth?.change || 0) >= 0 ? 'up' : 'down'}
            color="warning"
          />
          <MetricCard
            title="Revenue (KSH)"
            value={`${(analytics.totalRevenue / 1000).toFixed(0)}K`}
            iconName="cash"
            change={analytics.revenueGrowth.change}
            trend={analytics.revenueGrowth.change >= 0 ? 'up' : 'down'}
            color="primary"
            subtitle="This month"
          />
        </View>

        {/* Quick Actions */}
        <QuickActions navigation={navigation} />

        {/* Activity and Stats Row */}
        <View style={styles.twoColumnRow}>
          {/* Activity Timeline */}
          <View style={styles.column}>
            <ActivityTimeline activities={activities} />
          </View>

          {/* System Health / Additional Stats */}
          <View style={styles.column}>
            <View style={[styles.systemHealthCard, getShadow('md')]}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>System Health</Text>
                <View style={[styles.statusBadge, styles.statusHealthy]}>
                  <View style={styles.statusDot} />
                  <Text style={styles.statusText}>All Systems Operational</Text>
                </View>
              </View>

              <View style={styles.healthMetrics}>
                <HealthMetric
                  label="API Response Time"
                  value="145ms"
                  status="good"
                  icon="flash"
                />
                <HealthMetric
                  label="Database Status"
                  value="Healthy"
                  status="good"
                  icon="server"
                />
                <HealthMetric
                  label="Active Sessions"
                  value="234"
                  status="good"
                  icon="people"
                />
                <HealthMetric
                  label="Storage Used"
                  value="45%"
                  status="warning"
                  icon="folder"
                />
              </View>
            </View>

            {/* Recent Registrations */}
            <View style={[styles.recentCard, getShadow('md')]}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardTitle}>Recent Registrations</Text>
                <Text style={styles.cardSubtitle}>Last 7 days</Text>
              </View>

              <View style={styles.registrationsList}>
                {[
                  { day: 'Mon', count: 12 },
                  { day: 'Tue', count: 18 },
                  { day: 'Wed', count: 15 },
                  { day: 'Thu', count: 22 },
                  { day: 'Fri', count: 19 },
                  { day: 'Sat', count: 8 },
                  { day: 'Sun', count: 5 },
                ].map((item, index) => (
                  <View key={index} style={styles.registrationItem}>
                    <Text style={styles.registrationDay}>{item.day}</Text>
                    <View style={styles.registrationBar}>
                      <View
                        style={[
                          styles.registrationBarFill,
                          { width: `${(item.count / 22) * 100}%` },
                        ]}
                      />
                    </View>
                    <Text style={styles.registrationCount}>{item.count}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        </View>
      </ScrollView>
    </AdminLayout>
  );
};

// Health Metric Component
const HealthMetric = ({ label, value, status, icon }) => {
  const statusColors = {
    good: getColor('success.600'),
    warning: getColor('warning.600'),
    error: getColor('danger.600'),
  };

  return (
    <View style={styles.healthMetric}>
      <View style={styles.healthMetricHeader}>
        <Ionicons name={icon} size={16} color={getColor('gray.500')} />
        <Text style={styles.healthMetricLabel}>{label}</Text>
      </View>
      <Text style={[styles.healthMetricValue, { color: statusColors[status] }]}>
        {value}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: getColor('gray.50'),
  },
  contentContainer: {
    padding: getSpacing('lg'),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: getSpacing('xl'),
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: getColor('gray.900'),
    marginBottom: 4,
    ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
  },
  headerSubtitle: {
    fontSize: 14,
    color: getColor('gray.600'),
    ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
  },
  headerActions: {
    alignItems: 'flex-end',
  },
  lastUpdated: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  lastUpdatedText: {
    fontSize: 12,
    color: getColor('gray.500'),
    marginLeft: 4,
    ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -getSpacing('sm'),
    marginBottom: getSpacing('lg'),
  },
  twoColumnRow: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    gap: getSpacing('lg'),
  },
  column: {
    flex: 1,
    minWidth: Platform.OS === 'web' ? 400 : '100%',
  },
  systemHealthCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: getBorderRadius('lg'),
    padding: getSpacing('lg'),
    marginBottom: getSpacing('lg'),
  },
  recentCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: getBorderRadius('lg'),
    padding: getSpacing('lg'),
  },
  cardHeader: {
    marginBottom: getSpacing('lg'),
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: getColor('gray.900'),
    marginBottom: 4,
    ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
  },
  cardSubtitle: {
    fontSize: 14,
    color: getColor('gray.500'),
    ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: getSpacing('sm'),
    paddingVertical: 6,
    borderRadius: getBorderRadius('full'),
    marginTop: getSpacing('sm'),
  },
  statusHealthy: {
    backgroundColor: getColor('success.50'),
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: getColor('success.600'),
    marginRight: 6,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '500',
    color: getColor('success.700'),
    ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
  },
  healthMetrics: {
    gap: getSpacing('md'),
  },
  healthMetric: {
    paddingVertical: getSpacing('sm'),
    borderBottomWidth: 1,
    borderBottomColor: getColor('gray.100'),
  },
  healthMetricHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  healthMetricLabel: {
    fontSize: 13,
    color: getColor('gray.600'),
    marginLeft: 6,
    ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
  },
  healthMetricValue: {
    fontSize: 16,
    fontWeight: '600',
    ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
  },
  registrationsList: {
    gap: getSpacing('sm'),
  },
  registrationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: getSpacing('sm'),
  },
  registrationDay: {
    fontSize: 12,
    fontWeight: '500',
    color: getColor('gray.600'),
    width: 32,
    ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
  },
  registrationBar: {
    flex: 1,
    height: 8,
    backgroundColor: getColor('gray.100'),
    borderRadius: getBorderRadius('full'),
    overflow: 'hidden',
  },
  registrationBarFill: {
    height: '100%',
    backgroundColor: getColor('primary.500'),
    borderRadius: getBorderRadius('full'),
  },
  registrationCount: {
    fontSize: 12,
    fontWeight: '600',
    color: getColor('gray.700'),
    width: 24,
    textAlign: 'right',
    ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
  },
});

export default AdminPanelScreen;
